/* global fetch */
import { config } from '../config';
import { headers, APIResponse, stleaf, fetchAPI, SpanTag } from '../helpers';
import { CRS } from '@birdi/types/crs';
import {
  PointTypes,
  PointReference,
  SurveyInfo,
} from '@birdi/types/ground-references';

export interface UpdateSetFormData extends Partial<SurveyInfo> {
  id?: string /** id will be used as a trigger Update/Create */;
  label?: string;
  type?: PointTypes;
  cluster?: string | null;
  crs?: CRS;
  references?: PointReference[];
  visible?: boolean;
  selected?: boolean;
}

/** type for creating AND undating referenceSets */
interface UpdateFolderProps {
  siteId?: string;
  id?: string | number;
  label?: string;
  type?: string;
  position?: number;
  crs?: CRS;
  references?: PointReference[];
  surveyInfo?: SurveyInfo;
}
interface UpdatePointProps {
  id: string | number;
  label?: string;
  type?: PointTypes;
  cluster?: string | null;
}

type ReoderedLayers = {
  id: string | number;
  position: number;
};

/** Gets all references on mission ID as layers for the dnd-tree*/
export const getReferences = (
  st: SpanTag,
  siteId: string,
): Promise<APIResponse> => {
  const searchParams = new URLSearchParams({ siteId });
  const url = new URL(`${config.API_URL}/v1/references`);
  url.search = searchParams.toString();
  return fetchAPI(stleaf(st, '7udb4d'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

/** Creates a Reference set for site ID, creates references if passed as Array */
export const createReferenceSet = (
  st: SpanTag,
  props: UpdateFolderProps,
): Promise<APIResponse<any>> => {
  const url = `${config.API_URL}/v1/references`;
  return fetchAPI(stleaf(st, 'gcp0ae'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(props),
  });
};

/** Updates Reference or Reference Set with new props */
export const updateReference = (
  st: SpanTag,
  props: UpdateFolderProps | UpdatePointProps,
): Promise<APIResponse<any>> => {
  const url = `${config.API_URL}/v1/references`;
  return fetchAPI(stleaf(st, 'gcpg8n'), url, {
    method: 'PATCH',
    credentials: 'include',
    headers,
    body: JSON.stringify(props),
  });
};

/** Deletes Reference or Reference Set with new props */
export const deleteReferences = (
  st: SpanTag,
  layers: any[],
): Promise<APIResponse<any>> => {
  const url = `${config.API_URL}/v1/references`;
  return fetchAPI(stleaf(st, 'gcpg8n'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
    body: JSON.stringify(layers),
  });
};

/** Updates order (position) of Reference Sets */
export const updateReferenceSetsOrder = (
  st: SpanTag,
  props: ReoderedLayers[],
): Promise<APIResponse<Record<string, unknown>>[]> => {
  const url = `${config.API_URL}/v1/references`;
  const refPromises: Promise<APIResponse<Record<string, unknown>>>[] =
    props.map(async (prop) => {
      return await fetchAPI(stleaf(st, 'gcpg8n'), url, {
        method: 'PATCH',
        credentials: 'include',
        headers,
        body: JSON.stringify(prop),
      });
    });

  return Promise.all(refPromises); // Await all promises
};

/** Gets timeseries for a set of ground references from ground-References_observed table  */
export interface GetReferencesObservedParams {
  id: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}
export const getReferencesObserved = (
  st: SpanTag,
  params: GetReferencesObservedParams,
): Promise<APIResponse> => {
  const { id, ...args } = params;
  let url = `${config.API_URL}/v1/references/observed?set-id=${id}`;
  return fetchAPI(stleaf(st, 'quey7e'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(args),
  });
};

export interface ObservedReferencesReport {
  id: string;
  name: string;
  setId: string;
  s3Key?: string;
  status: 'pending' | 'complete' | 'failed';
  createdAt: string;
}

export interface GenerateObservedReportParams {
  refSetId: string;
  tab: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  title: string;
  refSetName: string;
  siteName: string;
}

export const generateObservedReport = (
  st: SpanTag,
  params: GenerateObservedReportParams,
): Promise<APIResponse<{ id: string }>> => {
  const { refSetId, title: name, ...args } = params;
  const url = `${config.API_URL}/v1/references/observed/reports?set-id=${refSetId}`;
  return fetchAPI(stleaf(st, 'coo28a'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ name, ...args }),
  });
};

export const listObservedReportsBySiteId = (
  st: SpanTag,
  siteId: string,
): Promise<APIResponse<ObservedReferencesReport[]>> => {
  const url = `${config.API_URL}/v1/references/observed/reports?site-id=${siteId}`;
  return fetchAPI(stleaf(st, 'f4atej'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getObservedReportURL = (
  st: SpanTag,
  reportId: string,
): Promise<APIResponse<string>> => {
  const url = `${config.API_URL}/v1/references/observed/reports/download?report-id=${reportId}`;
  return fetchAPI(stleaf(st, 'sahch5'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const deleteObservedReport = (
  st: SpanTag,
  reportId: string,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/references/observed/reports?report-id=${reportId}`;
  return fetchAPI(stleaf(st, 'oknt73'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};
